import { get, set, del } from 'idb-keyval';
import { type ChatMessageData } from '@/components/chat';

/**
 * Persistence utility for chat history.
 *
 * Phase 2A: backed by IndexedDB via idb-keyval.
 * Phase 2B transition: swap the internals of this file to use REST API calls
 * (optionally keeping IndexedDB as a local cache). No consumers need to change
 * because they interact only through this interface.
 */

type SerializedChatMap = Record<string, ChatMessageData[]>;

function storageKey(workflowId: string): string {
  return `chat:${workflowId}`;
}

/**
 * Persist the full per-node/workflow message cache to IndexedDB.
 * Serialises the Map into a plain object for storage.
 */
export async function saveChatHistory(
  workflowId: string,
  messageCache: Map<string, ChatMessageData[]>,
): Promise<void> {
  const serialized: SerializedChatMap = {};
  for (const [nodeId, messages] of messageCache.entries()) {
    if (messages.length > 0) {
      serialized[nodeId] = messages;
    }
  }
  await set(storageKey(workflowId), serialized);
}

/**
 * Load the persisted message cache from IndexedDB.
 * Returns null when no data exists for the workflow.
 */
export async function loadChatHistory(
  workflowId: string,
): Promise<Map<string, ChatMessageData[]> | null> {
  const raw = await get<SerializedChatMap>(storageKey(workflowId));
  if (!raw || typeof raw !== 'object') return null;

  const map = new Map<string, ChatMessageData[]>();
  for (const [nodeId, messages] of Object.entries(raw)) {
    if (Array.isArray(messages)) {
      map.set(
        nodeId,
        messages.map((m) => ({
          ...m,
          timestamp: m.timestamp ? new Date(m.timestamp) : undefined,
        })),
      );
    }
  }
  return map.size > 0 ? map : null;
}

/**
 * Delete persisted chat history for a workflow.
 */
export async function clearChatHistory(workflowId: string): Promise<void> {
  await del(storageKey(workflowId));
}
